import { useEffect, useState } from 'react'
import Script from 'next/script'
import { useFeatureFlips } from '@mc/feature-flip-react'
import { getAnonymousId } from '@mc/track-event'
import { getAccountId } from '@mc/user-info'

// The vendor's CHEQ invocation tag, kept verbatim from the GTM snippet. The only
// adaptation is `uvid`: instead of GTM template variables, React resolves it
// (account id, falling back to the Segment anonymous id) and passes it via the
// `data-uvid` attribute that this snippet reads back off its own tag.
const CHEQ_INVOCATION_TAG = `
  var script = document.createElement('script');
  script.src = 'https://ob.protoroundprince.com/i/d97c173e4e998836f40af7f867c514c3.js';
  script.setAttribute('data-uvid', document.getElementById('cheq').getAttribute('data-uvid'));
  script.setAttribute('data-ch', 'cheq4ppc');
  script.setAttribute('class', 'ct_clicktrue_55306');
  document.body.appendChild(script);
`

// Vendor-provided fallback for clients with JavaScript disabled (a common bot
// evasion). Mirrors the GTM noscript-iframe pattern.
const CHEQ_NOSCRIPT_SRC =
  'https://obs.protoroundprince.com/ns/d97c173e4e998836f40af7f867c514c3.html?ch=cheq4ppc'

export const Cheq = () => {
  // https://app.growthbook.io/features/cheq_script
  const { cheq_script: enabled } = useFeatureFlips()
  const [uvid, setUvid] = useState<string | null>(null)

  useEffect(() => {
    // CHEQ fires pre-consent for ALL users, including GDPR/opt-in regions, for the
    // sole purpose of click-fraud detection — approved by Legal (Slack, 2026-06-25),
    // conditioned on no other tracking changes. It is independent of every other
    // 3rd-party tag (Segment, GTM, Meta), which remain consent-gated. Logged-out GDPR
    // users have no Segment anonymous id pre-consent, so uvid falls back to ''; CHEQ
    // still fires. Once uvid is set we keep it, which also prevents double injection.
    if (!enabled || uvid !== null) return
    const accountId = getAccountId()
    setUvid(accountId ? String(accountId) : getAnonymousId() || '')
  }, [enabled, uvid])

  if (!enabled) return null

  return (
    <>
      {/* JS-disabled fallback. Server-rendered (the cheq_script flag resolves at SSR)
          so it exists in the initial HTML; the browser only activates it when JS is
          off. Rendered independently of uvid, which is client-only state. */}
      <noscript id='cheq-noscript'>
        {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
        <iframe
          src={CHEQ_NOSCRIPT_SRC}
          width='0'
          height='0'
          style={{ display: 'none' }}
        />
      </noscript>
      {/* Relies on next/script's default afterInteractive strategy (do not switch to
          lazyOnload): CHEQ detects click fraud on paid traffic, so it must fire on as
          many visits as possible — including fast bounces that lazyOnload's post-load
          idle callback can miss. afterInteractive still injects client-side after
          hydration, so it does not block LCP. */}
      {uvid !== null && (
        <Script
          id='cheq'
          data-uvid={uvid}
          dangerouslySetInnerHTML={{ __html: CHEQ_INVOCATION_TAG }}
        />
      )}
    </>
  )
}
